import { gql, useMutation, useQuery } from '@apollo/client'
import flatten from 'lodash/flatten'
import { useMemo } from 'react'

import {
  ArtistSubscriptionPlan,
  ArtistSubscriptionPlanQuery,
  ArtistSubscriptionPlanQueryVariables,
  PaymentMethod,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'
import { LotteryCreateValues } from './template/LotteryCreateForm'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router, generatePath, routerPages } =
    useRouter<{ preSaleType?: string; eventId: string }>()
  const { showMessage, showError } = useMessage()
  const { t } = useTranslation('consolePreSaleCreate')
  const { preSaleType } = router.query || {}

  const [createPreSale, { loading: submitting }] = useMutation(CREATE_PRE_SALE)

  const { data } = useQuery<
    ArtistSubscriptionPlanQuery,
    ArtistSubscriptionPlanQueryVariables
  >(ArtistSubscriptionPlan)

  const plans = useMemo(() => {
    if (data?.artist?.subscription?.products?.length) {
      return flatten(
        data?.artist?.subscription?.products.map(product => {
          return product.plans.map(plan => ({ ...plan, title: product.title }))
        })
      )
    }
    if (data?.artist?.subscription?.plans?.length) {
      return data.artist.subscription.plans
    }
    return []
  }, [data?.artist?.subscription?.plans, data?.artist?.subscription?.products])

  return {
    state: {
      artistId: user?.uid || '',
      preSaleType,
      submitting,
      plans,
    },
    actions: {
      onSubmit: async values => {
        const {
          description,
          endedAt,
          isPublished,
          note,
          startedAt,
          title,
          purchesablePlanIds,
          isPremium,
        } = values
        await createPreSale({
          variables: {
            inputs: {
              description,
              endedAt,
              isPublished,
              note,
              startedAt,
              title,
              isPremium,
              type:
                preSaleType === 'firstComeFirstServed'
                  ? PreSaleType.FirstComeFirstServed
                  : PreSaleType.Lottery,
              paymentMethods:
                values.paymentMethods === 'both'
                  ? [PaymentMethod.CreditCard, PaymentMethod.Konbini]
                  : [values.paymentMethods],
              lottery:
                preSaleType === PreSaleType.Lottery
                  ? {
                      applicationEndedAt: (values as LotteryCreateValues)
                        .applicationEndedAt,
                      applicationStartedAt: (values as LotteryCreateValues)
                        .applicationStartedAt,
                      maxChoice: (values as LotteryCreateValues).maxChoice,
                      purchasableCount: values.purchasableCount,
                      purchasableEndedAt: values.purchasableEndedAt,
                      purchasableStartedAt: values.purchasableStartedAt,
                      resultNoticedAt: (values as LotteryCreateValues)
                        .resultNoticedAt,
                    }
                  : undefined,
              firstComeFirstServed:
                preSaleType === 'firstComeFirstServed'
                  ? {
                      purchasableCount: values.purchasableCount,
                      purchasableEndedAt: values.purchasableEndedAt,
                      purchasableStartedAt: values.purchasableStartedAt,
                    }
                  : undefined,
              eventId: router.query.eventId,
              itemIds: [],
              purchesablePlanIds,
            },
          },
        })
          .then(() => {
            showMessage(t('successMessage', '先行販売チケットを作成しました'))
            router.push(
              generatePath(routerPages.ConsoleShokufesEventDetail, {
                eventId: router.query.eventId,
              })
            )
          })
          .catch(showError)
      },
    },
  }
})

const CREATE_PRE_SALE = gql`
  mutation createPreSale($inputs: ArtistCreatePreSaleInput!) {
    artistCreatePreSale(inputs: $inputs) {
      id
    }
  }
`
